import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  InputGroup,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  X,
  Save,
  Minus,
  Plus,
  ExternalLink,
  Copy,
  CheckCircle,
  AlertTriangle,
  AlertOctagon,
} from "react-feather";
import { Link } from "react-router";
import { useGet, usePost, usePut, api, apiErrorMessage } from "../../api";
import { useAuth } from "../../hooks/auth";
import {
  IKindredDealDetail,
  IKindredDealDomainInput,
  IKindredCouponDetail,
  IKindredCouponApplyToSiblingsResult,
  IKindredCouponSiblingsResponse,
  IKindredOfferDetail,
  IKindredBrandReference,
  ICouponResultsResponse,
  ICouponResultWithMerchant,
  KindredPublishStatus,
  KindredPromoType,
  KindredPromoCoverage,
  KindredPromoScope,
  KindredPromoChannel,
  KindredDiscountType,
  KindredCouponCoverage,
  CouponOverridesFlag,
  CampaignOverridesFlag,
  OfferOverridesFlag,
  KINDRED_NON_MONETARY_VALUES,
} from "../../interfaces";
import { BrandTypeahead } from "./brand-typeahead";
import { useEditSession } from "./edit-session-context";
import { format } from "date-fns";

interface ReviewDetailPanelProps {
  entityType: "deal" | "coupon" | "offer" | null;
  entityId: string | null;
  countryCode?: string | null;
  onClose: () => void;
  /** Fired after a direct (non-staged) save so hosts can refresh their lists. */
  onSaved?: () => void;
  readOnly?: boolean;
}

const publishedStatusOptions = [
  { value: KindredPublishStatus.Draft, label: "Draft" },
  { value: KindredPublishStatus.AwaitingApproval, label: "Awaiting Approval" },
  { value: KindredPublishStatus.Approved, label: "Approved" },
  { value: KindredPublishStatus.Deactivated, label: "Deactivated" },
];

/** The kindred update API requires a non-empty audit comment. */
const DEFAULT_SAVE_COMMENT = "Updated from the QA workbench";

/** Reason-for-update box shown when saving directly (no staging session). */
const SaveCommentField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  error?: { message?: string; data?: any } | null;
}> = ({ value, onChange, error }) => (
  <div className="mb-2">
    <Form.Control
      size="sm"
      placeholder={`Reason for update (defaults to "${DEFAULT_SAVE_COMMENT}")`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
    {error && (
      <Alert variant="danger" className="py-1 px-2 mt-1 mb-0 small">
        {apiErrorMessage(error, "Save failed.")}
      </Alert>
    )}
  </div>
);

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  try {
    return format(new Date(dateStr), "yyyy-MM-dd HH:mm");
  } catch {
    return dateStr;
  }
};

// ── Discounts JSON editor ────────────────────────────────

interface DiscountEntry {
  discountValue?: number | null;
  maxDiscountValue?: number | null;
  minimumSpend?: number | null;
}

export const DiscountsEditor: React.FC<{
  value: string | null;
  onChange: (val: string | null) => void;
}> = ({ value, onChange }) => {
  const parseEntries = (raw: string | null): DiscountEntry[] => {
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  };

  const entries = parseEntries(value);

  const update = (newEntries: DiscountEntry[]) => {
    onChange(newEntries.length > 0 ? JSON.stringify(newEntries) : null);
  };

  const addEntry = () => {
    update([
      ...entries,
      { discountValue: null, maxDiscountValue: null, minimumSpend: null },
    ]);
  };

  const removeEntry = (idx: number) => {
    update(entries.filter((_, i) => i !== idx));
  };

  const updateEntry = (
    idx: number,
    field: keyof DiscountEntry,
    val: string
  ) => {
    const updated = [...entries];
    updated[idx] = {
      ...updated[idx],
      [field]: val === "" ? null : Number(val),
    };
    update(updated);
  };

  return (
    <div>
      {entries.map((entry, i) => (
        <div key={i} className="d-flex gap-1 align-items-center mb-1">
          <Form.Control
            size="sm"
            type="number"
            placeholder="Value"
            style={{ width: 75 }}
            value={entry.discountValue ?? ""}
            onChange={(e) => updateEntry(i, "discountValue", e.target.value)}
          />
          <Form.Control
            size="sm"
            type="number"
            placeholder="Max"
            style={{ width: 75 }}
            value={entry.maxDiscountValue ?? ""}
            onChange={(e) => updateEntry(i, "maxDiscountValue", e.target.value)}
          />
          <Form.Control
            size="sm"
            type="number"
            placeholder="Min spend"
            style={{ width: 85 }}
            value={entry.minimumSpend ?? ""}
            onChange={(e) => updateEntry(i, "minimumSpend", e.target.value)}
          />
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => removeEntry(i)}
            style={{ padding: "2px 4px" }}
          >
            <Minus size={10} />
          </Button>
        </div>
      ))}
      <Button
        variant="outline-primary"
        size="sm"
        onClick={addEntry}
        style={{ fontSize: 11 }}
      >
        <Plus size={10} className="me-1" />
        Add
      </Button>
    </div>
  );
};

// ── Shared metadata editor for coupon / offer forms ──────

const parseNullableNumber = (val: string): number | null =>
  val === "" ? null : Number(val);

const parseNullableBool = (val: string): boolean | null =>
  val === "" ? null : val === "true";

export const MetadataEditor: React.FC<{
  metadata: Record<string, any>;
  onChange: (m: Record<string, any>) => void;
}> = ({ metadata, onChange }) => {
  const update = (field: string, value: any) => {
    onChange({ ...metadata, [field]: value });
  };

  return (
    <Card className="mb-2">
      <Card.Header className="py-1 small fw-semibold">Metadata</Card.Header>
      <Card.Body className="py-2">
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Promo Type</Form.Label>
          <Form.Select
            size="sm"
            value={metadata.promoType ?? ""}
            onChange={(e) =>
              update("promoType", parseNullableNumber(e.target.value))
            }
          >
            <option value="">—</option>
            <option value={KindredPromoType.None}>None</option>
            <option value={KindredPromoType.Monetary}>Monetary</option>
            <option value={KindredPromoType.NonMonetary}>Non-Monetary</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Coverage</Form.Label>
          <Form.Select
            size="sm"
            value={metadata.coverage ?? ""}
            onChange={(e) =>
              update("coverage", parseNullableNumber(e.target.value))
            }
          >
            <option value="">—</option>
            <option value={KindredPromoCoverage.None}>None</option>
            <option value={KindredPromoCoverage.SiteWide}>Site-Wide</option>
            <option value={KindredPromoCoverage.Category}>Category</option>
            <option value={KindredPromoCoverage.Product}>Product</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Scope</Form.Label>
          <Form.Select
            size="sm"
            value={metadata.scope ?? ""}
            onChange={(e) =>
              update("scope", parseNullableNumber(e.target.value))
            }
          >
            <option value="">—</option>
            <option value={KindredPromoScope.None}>None</option>
            <option value={KindredPromoScope.New}>New Customers</option>
            <option value={KindredPromoScope.Existing}>
              Existing Customers
            </option>
            <option value={KindredPromoScope.All}>All Customers</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Channel</Form.Label>
          <Form.Select
            size="sm"
            value={metadata.channel ?? ""}
            onChange={(e) =>
              update("channel", parseNullableNumber(e.target.value))
            }
          >
            <option value="">—</option>
            <option value={KindredPromoChannel.None}>None</option>
            <option value={KindredPromoChannel.Online}>Online</option>
            <option value={KindredPromoChannel.InStore}>In-Store</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Discount Type</Form.Label>
          <Form.Select
            size="sm"
            value={metadata.discountType ?? ""}
            onChange={(e) =>
              update("discountType", parseNullableNumber(e.target.value))
            }
          >
            <option value="">—</option>
            <option value={KindredDiscountType.None}>None</option>
            <option value={KindredDiscountType.Percentage}>Percentage</option>
            <option value={KindredDiscountType.Fixed}>Fixed</option>
            <option value={KindredDiscountType.Others}>Others</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Discounts</Form.Label>
          <DiscountsEditor
            value={metadata.discounts ?? null}
            onChange={(val) => update("discounts", val)}
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Non-Monetary Value</Form.Label>
          <Form.Select
            size="sm"
            value={metadata.nonMonetaryValue ?? ""}
            onChange={(e) => update("nonMonetaryValue", e.target.value || null)}
          >
            <option value="">—</option>
            {KINDRED_NON_MONETARY_VALUES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Language</Form.Label>
          <Form.Control
            size="sm"
            value={metadata.language ?? ""}
            onChange={(e) => update("language", e.target.value || null)}
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Currency</Form.Label>
          <Form.Control
            size="sm"
            value={metadata.currency ?? ""}
            onChange={(e) => update("currency", e.target.value || null)}
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Product Category</Form.Label>
          <Form.Control
            size="sm"
            value={metadata.productCategory ?? ""}
            onChange={(e) => update("productCategory", e.target.value || null)}
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Raw Category</Form.Label>
          <Form.Control
            size="sm"
            value={metadata.rawCategory ?? ""}
            onChange={(e) => update("rawCategory", e.target.value || null)}
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Stackable</Form.Label>
          <Form.Select
            size="sm"
            value={
              metadata.isStackable == null ? "" : String(metadata.isStackable)
            }
            onChange={(e) =>
              update("isStackable", parseNullableBool(e.target.value))
            }
          >
            <option value="">—</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Requires Review</Form.Label>
          <Form.Select
            size="sm"
            value={
              metadata.requiresReview == null
                ? ""
                : String(metadata.requiresReview)
            }
            onChange={(e) =>
              update("requiresReview", parseNullableBool(e.target.value))
            }
          >
            <option value="">—</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Reviewed</Form.Label>
          <Form.Select
            size="sm"
            value={
              metadata.isReviewed == null ? "" : String(metadata.isReviewed)
            }
            onChange={(e) =>
              update("isReviewed", parseNullableBool(e.target.value))
            }
          >
            <option value="">—</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-0">
          <Form.Label className="small mb-0">Reasoning</Form.Label>
          <Form.Control
            as="textarea"
            size="sm"
            rows={2}
            value={metadata.reasoning ?? ""}
            onChange={(e) => update("reasoning", e.target.value || null)}
          />
        </Form.Group>
      </Card.Body>
    </Card>
  );
};

// ── Tag List Editor (domains / countries) ────────────────

export const TagListEditor: React.FC<{
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  lockedItems?: string[];
  /** Country codes are stored upper case; domains must stay lower case. */
  normalize?: "upper" | "lower" | "none";
}> = ({
  label,
  items,
  onChange,
  placeholder,
  lockedItems,
  normalize = "upper",
}) => {
  const [input, setInput] = useState("");
  const lockedSet = new Set(lockedItems ?? []);
  const removable = items.filter((i) => !lockedSet.has(i));

  const normalizeOne = (val: string) =>
    normalize === "lower"
      ? val.toLowerCase()
      : normalize === "upper"
        ? val.toUpperCase()
        : val;

  // One entry per line, comma or space, so a list pasted from a spreadsheet
  // goes in as a list instead of a single nonsense tag.
  const addItems = () => {
    const added = input
      .split(/[\s,;]+/)
      .map((v) => normalizeOne(v.trim()))
      .filter(Boolean);
    if (added.length > 0) {
      const next = [...items];
      for (const val of added) {
        if (!next.includes(val)) next.push(val);
      }
      if (next.length !== items.length) onChange(next);
    }
    setInput("");
  };

  const removeItem = (item: string) => {
    onChange(items.filter((i) => i !== item));
  };

  return (
    <Card className="mb-2">
      <Card.Header className="py-1 small fw-semibold d-flex justify-content-between align-items-center">
        <span>
          {label} ({items.length})
        </span>
        {removable.length > 0 && (
          <Button
            variant="link"
            size="sm"
            className="p-0 text-decoration-none"
            style={{ fontSize: "0.75rem" }}
            title={`Remove all ${removable.length} entries at once`}
            onClick={() => onChange(items.filter((i) => lockedSet.has(i)))}
          >
            Remove all
          </Button>
        )}
      </Card.Header>
      <Card.Body className="py-2">
        <div className="d-flex flex-wrap gap-1 mb-2">
          {items.length === 0 && <span className="text-muted small">None</span>}
          {items.map((item) => (
            <Badge
              key={item}
              bg="light"
              text="dark"
              className="d-flex align-items-center gap-1"
            >
              {item}
              {!lockedSet.has(item) && (
                <X
                  size={10}
                  style={{ cursor: "pointer" }}
                  onClick={() => removeItem(item)}
                />
              )}
            </Badge>
          ))}
        </div>
        <InputGroup size="sm">
          <Form.Control
            placeholder={placeholder || "Add..."}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addItems();
              }
            }}
          />
          <Button
            variant="outline-primary"
            onClick={addItems}
            disabled={!input.trim()}
          >
            <Plus size={12} />
          </Button>
        </InputGroup>
        <Form.Text className="text-muted" style={{ fontSize: "0.72rem" }}>
          Paste or type several at once — separate them with commas, spaces or
          new lines.
        </Form.Text>
      </Card.Body>
    </Card>
  );
};

// ── Deal Domain Editor (domains + their validated flag) ──

/**
 * Deal domains, each with the `validated` flag that decides whether the domain
 * is trusted for coupon testing. Only deal (campaign) domains carry one —
 * coupon and offer domains stay on the plain TagListEditor.
 */
export const DealDomainEditor: React.FC<{
  items: IKindredDealDomainInput[];
  onChange: (items: IKindredDealDomainInput[]) => void;
}> = ({ items, onChange }) => {
  const [input, setInput] = useState("");
  // Domains added by hand are ones someone has just checked, so they go in validated.
  const [addAsValidated, setAddAsValidated] = useState(true);

  const addItems = () => {
    const added = input
      .split(/[\s,;]+/)
      .map((v) => v.trim().toLowerCase())
      .filter(Boolean);
    if (added.length > 0) {
      const next = [...items];
      for (const domain of added) {
        if (next.some((i) => i.domain.toLowerCase() === domain)) continue;
        next.push({ domain, validated: addAsValidated });
      }
      if (next.length !== items.length) onChange(next);
    }
    setInput("");
  };

  const setValidated = (domain: string, validated: boolean) =>
    onChange(
      items.map((i) => (i.domain === domain ? { ...i, validated } : i))
    );

  const setAllValidated = (validated: boolean) =>
    onChange(items.map((i) => ({ ...i, validated })));

  return (
    <Card className="mb-2">
      <Card.Header className="py-1 small fw-semibold d-flex justify-content-between align-items-center">
        <span>Domains ({items.length})</span>
        {items.length > 0 && (
          <span className="d-flex gap-2">
            <Button
              variant="link"
              size="sm"
              className="p-0 text-decoration-none"
              style={{ fontSize: "0.75rem" }}
              title="Mark every domain below as validated"
              onClick={() => setAllValidated(true)}
            >
              Validate all
            </Button>
            <Button
              variant="link"
              size="sm"
              className="p-0 text-decoration-none text-secondary"
              style={{ fontSize: "0.75rem" }}
              title="Clear the validated flag on every domain below"
              onClick={() => setAllValidated(false)}
            >
              Unvalidate all
            </Button>
            <Button
              variant="link"
              size="sm"
              className="p-0 text-decoration-none text-danger"
              style={{ fontSize: "0.75rem" }}
              title={`Remove all ${items.length} domains at once`}
              onClick={() => onChange([])}
            >
              Remove all
            </Button>
          </span>
        )}
      </Card.Header>
      <Card.Body className="py-2">
        {items.length === 0 ? (
          <div className="text-muted small mb-2">None</div>
        ) : (
          <Table size="sm" className="mb-2 small">
            <thead>
              <tr>
                <th>Domain</th>
                <th style={{ width: "6rem" }}>Validated</th>
                <th style={{ width: "2rem" }} />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.domain}>
                  <td className="align-middle">{item.domain}</td>
                  <td className="align-middle">
                    <Form.Check
                      type="switch"
                      checked={item.validated ?? false}
                      title="Validated domains are trusted for coupon testing"
                      onChange={(e) =>
                        setValidated(item.domain, e.target.checked)
                      }
                    />
                  </td>
                  <td className="align-middle">
                    <X
                      size={12}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        onChange(items.filter((i) => i.domain !== item.domain))
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <InputGroup size="sm">
          <Form.Control
            placeholder="Add domain..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addItems();
              }
            }}
          />
          <Button
            variant="outline-primary"
            onClick={addItems}
            disabled={!input.trim()}
          >
            <Plus size={12} />
          </Button>
        </InputGroup>
        <div className="d-flex justify-content-between align-items-center mt-1">
          <Form.Text className="text-muted" style={{ fontSize: "0.72rem" }}>
            Paste or type several at once — separate them with commas, spaces
            or new lines.
          </Form.Text>
          <Form.Check
            type="checkbox"
            className="small text-muted"
            id="deal-domain-add-validated"
            label="Add as validated"
            checked={addAsValidated}
            onChange={(e) => setAddAsValidated(e.target.checked)}
          />
        </div>
      </Card.Body>
    </Card>
  );
};

// ── Override Flags Editor (bitwise checkboxes) ───────────

export const OverrideFlagsEditor: React.FC<{
  value: number;
  onChange?: (val: number) => void;
  flagEnum: Record<string, string | number>;
  readOnly?: boolean;
}> = ({ value, onChange, flagEnum, readOnly }) => {
  const flags = Object.entries(flagEnum).filter(
    ([key, val]) => typeof val === "number" && val !== 0 && isNaN(Number(key))
  ) as [string, number][];

  const toggle = (bit: number) => {
    onChange?.(value ^ bit);
  };

  return (
    <Card className="mb-2">
      <Card.Header className="py-1 small fw-semibold">
        Override Flags
      </Card.Header>
      <Card.Body className="py-2">
        {flags.map(([name, bit]) => (
          <Form.Check
            key={name}
            type="checkbox"
            label={<span className="small">{name}</span>}
            checked={(value & bit) !== 0}
            onChange={() => toggle(bit)}
            disabled={readOnly}
          />
        ))}
      </Card.Body>
    </Card>
  );
};

export const ReviewDetailPanel: React.FC<ReviewDetailPanelProps> = ({
  entityType,
  entityId,
  countryCode,
  onClose,
  onSaved,
  readOnly = false,
}) => {
  if (!entityType || !entityId) return null;

  return (
    <div className="d-flex flex-column h-100">
      <div className="d-flex justify-content-between align-items-center p-2 border-bottom">
        <span className="fw-semibold small">
          <Badge
            bg={
              entityType === "deal"
                ? "primary"
                : entityType === "coupon"
                  ? "info"
                  : "warning"
            }
          >
            {entityType}
          </Badge>
          <span className="ms-2">{entityId.substring(0, 12)}...</span>
        </span>
        <Button variant="link" size="sm" onClick={onClose}>
          <X size={16} />
        </Button>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: 8 }}>
        <fieldset disabled={readOnly}>
          {entityType === "deal" && (
            <DealEditForm id={entityId} onSaved={onSaved} />
          )}
          {entityType === "coupon" && (
            <CouponEditForm
              id={entityId}
              countryCode={countryCode}
              onSaved={onSaved}
            />
          )}
          {entityType === "offer" && (
            <OfferEditForm id={entityId} onSaved={onSaved} />
          )}
        </fieldset>
      </div>
    </div>
  );
};

// ── Deal Edit Form ───────────────────────────────────────

const DealEditForm: React.FC<{ id: string; onSaved?: () => void }> = ({
  id,
  onSaved,
}) => {
  const { data, loading, error, refetch } = useGet<IKindredDealDetail>(
    `kindred/deals/${id}`,
    true
  );
  const { addEdit, pendingEdits, isStaging } = useEditSession();
  const saveEntity = usePut<unknown>();
  const [saveComment, setSaveComment] = useState("");
  const [form, setForm] = useState<Record<string, any>>({});

  useEffect(() => {
    if (data) {
      setForm({
        name: data.name,
        brandId: data.brandId,
        brandName: data.brandName,
        publishedStatus: data.publishedStatus,
        suspended: data.suspended,
        source: data.source,
        startDate: data.startDate,
        endDate: data.endDate,
        domains: data.domainDetails?.map((d) => d.domain) || data.domains || [],
        overridesFlag: data.overridesFlag ?? 0,
      });
    }
  }, [data]);

  if (loading) return <Spinner animation="border" size="sm" />;
  if (error) return <Alert variant="danger">Error loading deal</Alert>;
  if (!data) return null;

  const handleStage = async () => {
    const changes: Record<string, any> = {};
    if (form.name !== data.name) changes.name = form.name;
    if (form.brandId !== data.brandId) changes.brandId = form.brandId;
    if (form.publishedStatus !== data.publishedStatus)
      changes.publishedStatus = form.publishedStatus;
    if (form.suspended !== data.suspended) changes.suspended = form.suspended;
    if (form.source !== data.source) changes.source = form.source;
    if (form.startDate !== data.startDate) changes.startDate = form.startDate;
    if (form.endDate !== data.endDate) changes.endDate = form.endDate;
    const origDomains =
      data.domainDetails?.map((d) => d.domain) || data.domains || [];
    if (JSON.stringify(form.domains) !== JSON.stringify(origDomains))
      changes.domains = form.domains;
    if (form.overridesFlag !== (data.overridesFlag ?? 0))
      changes.overridesFlag = form.overridesFlag;

    if (!isStaging) {
      if (Object.keys(changes).length === 0) return;
      await saveEntity.execute(`kindred/deals/${data.id}`, {
        data: changes,
        comment: saveComment.trim() || DEFAULT_SAVE_COMMENT,
      });
      setSaveComment("");
      refetch();
      onSaved?.();
      return;
    }

    addEdit({
      type: "deal",
      id: data.id,
      entityName: data.name || data.id,
      original: {
        name: data.name,
        brandId: data.brandId,
        publishedStatus: data.publishedStatus,
        suspended: data.suspended,
        source: data.source,
        startDate: data.startDate,
        endDate: data.endDate,
        domains: origDomains,
        overridesFlag: data.overridesFlag ?? 0,
      },
      changes,
    });
  };

  const isPending = pendingEdits.has(`deal:${id}`);

  return (
    <>
      <div
        className="d-flex gap-2 mb-2"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "white",
          paddingBottom: 4,
        }}
      >
        <Link
          to={`/kindred/deals/${id}`}
          className="btn btn-outline-secondary btn-sm"
          target="_blank"
        >
          <ExternalLink size={12} className="me-1" />
          Full Detail
        </Link>
        <Link
          to={`/kindred/deals/${id}/audit`}
          className="btn btn-outline-secondary btn-sm"
          target="_blank"
        >
          <ExternalLink size={12} className="me-1" />
          Audit Trail
        </Link>
        <Button
          variant={isPending ? "outline-success" : "primary"}
          size="sm"
          onClick={handleStage}
          disabled={saveEntity.loading}
        >
          <Save size={12} className="me-1" />
          {!isStaging
            ? saveEntity.loading
              ? "Saving..."
              : "Save"
            : isPending
              ? "Update Staged"
              : "Stage Changes"}
        </Button>
        {isPending && (
          <Badge bg="success" className="align-self-center">
            Staged
          </Badge>
        )}
      </div>

      {!isStaging && (
        <SaveCommentField
          value={saveComment}
          onChange={setSaveComment}
          error={saveEntity.error}
        />
      )}

      <Table size="sm" borderless className="small mb-2">
        <tbody>
          <tr>
            <td className="text-muted">Start Date</td>
            <td>
              <Form.Control
                size="sm"
                type="datetime-local"
                value={
                  form.startDate
                    ? form.startDate.substring(0, 16)
                    : data.startDate
                      ? data.startDate.substring(0, 16)
                      : ""
                }
                onChange={(e) =>
                  setForm({
                    ...form,
                    startDate: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td className="text-muted">End Date</td>
            <td>
              <Form.Control
                size="sm"
                type="datetime-local"
                value={
                  form.endDate
                    ? form.endDate.substring(0, 16)
                    : data.endDate
                      ? data.endDate.substring(0, 16)
                      : ""
                }
                onChange={(e) =>
                  setForm({
                    ...form,
                    endDate: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td className="text-muted">Countries</td>
            <td>{data.countries?.join(", ") || "—"}</td>
          </tr>
        </tbody>
      </Table>

      <BrandTypeahead
        brandId={form.brandId || data.brandId}
        brandName={form.brandName || data.brandName}
        onChange={(brandId, brandName) =>
          setForm({ ...form, brandId, brandName })
        }
      />

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Name</Form.Label>
        <Form.Control
          size="sm"
          value={form.name || ""}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Published Status</Form.Label>
        <Form.Select
          size="sm"
          value={form.publishedStatus ?? ""}
          onChange={(e) =>
            setForm({ ...form, publishedStatus: Number(e.target.value) })
          }
        >
          {publishedStatusOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Check
          type="switch"
          label="Suspended"
          checked={!!form.suspended}
          onChange={(e) => setForm({ ...form, suspended: e.target.checked })}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Source</Form.Label>
        <Form.Control
          size="sm"
          value={form.source || ""}
          onChange={(e) => setForm({ ...form, source: e.target.value })}
        />
      </Form.Group>

      <TagListEditor
        label="Domains"
        normalize="lower"
        items={form.domains || []}
        onChange={(domains) => setForm({ ...form, domains })}
        placeholder="Add domain..."
      />

      <OverrideFlagsEditor
        value={form.overridesFlag ?? 0}
        onChange={(overridesFlag) => setForm({ ...form, overridesFlag })}
        flagEnum={CampaignOverridesFlag}
      />
    </>
  );
};

// ── Coupon Edit Form ─────────────────────────────────────

// ── Apply one code's edits across its identical siblings ──────────────────
// A brand usually carries the same code as a separate row per domain/geo.
// Editing one and pushing it to the rest is the time-saver testers asked for,
// but nothing is written until they have seen the exact rows it will touch.

/** Fields that identify a row rather than describe the offer — never copied,
 *  because copying them would merge or destroy the sibling. Mirrored
 *  server-side; this list only shapes the picker. */
const SAME_CODE_COPYABLE_FIELDS = [
  "publishedStatus",
  "reason",
  "discount",
  "summary",
  "priority",
  "coverage",
  "lastTestedAt",
  "validFrom",
  "validTo",
  "overridesFlag",
  "metadata",
] as const;

type SameCodeField = (typeof SAME_CODE_COPYABLE_FIELDS)[number];

const FIELD_LABELS: Record<SameCodeField, string> = {
  publishedStatus: "Status",
  reason: "Deactivation reason",
  discount: "Discount",
  summary: "Summary",
  priority: "Priority",
  coverage: "Coverage",
  lastTestedAt: "Last tested",
  validFrom: "Valid from",
  validTo: "Valid to",
  overridesFlag: "Override flags",
  metadata: "Metadata",
};

const describeValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "(empty)";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const ApplyToSameCodeModal: React.FC<{
  show: boolean;
  couponId: string;
  /** What is on screen now — the values that get copied. */
  form: Record<string, any>;
  /** Fields edited but not yet saved; these are ticked by default. */
  unsavedFields: string[];
  comment: string;
  /** Saves the source coupon itself. Run first when there are unsaved edits,
   *  so the code being copied FROM ends up matching the ones copied TO. */
  saveSource: () => Promise<void>;
  onHide: () => void;
  onApplied: () => void;
}> = ({
  show,
  couponId,
  form,
  unsavedFields,
  comment,
  saveSource,
  onHide,
  onApplied,
}) => {
  const { data, loading, error } = useGet<IKindredCouponSiblingsResponse>(
    show ? `kindred/coupons/${couponId}/same-code` : null,
    true
  );
  const apply = usePost<IKindredCouponApplyToSiblingsResult>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedFields, setSelectedFields] = useState<SameCodeField[]>([]);
  const [result, setResult] =
    useState<IKindredCouponApplyToSiblingsResult | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);

  const siblings = data?.siblings ?? [];

  // Rows are pre-ticked (the point is "change them all"); fields default to
  // whatever is unsaved, which is the common case of edit-then-push. Saving
  // first is just as valid, so nothing is forced — the tester picks.
  useEffect(() => {
    if (!show) return;
    setSelectedIds(siblings.map((c) => c.id));
    setSelectedFields(
      SAME_CODE_COPYABLE_FIELDS.filter((f) => unsavedFields.includes(f))
    );
    setResult(null);
    setApplyError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, data]);

  const confirm = async () => {
    setApplyError(null);
    try {
      // Save the source first when it has pending edits, or the code being
      // copied from would end up the odd one out.
      if (unsavedFields.length > 0) await saveSource();

      const res = await apply.execute(
        `kindred/coupons/${couponId}/same-code/apply`,
        {
          data: Object.fromEntries(
            selectedFields.map((f) => [f, form[f] ?? null])
          ),
          comment: comment.trim() || DEFAULT_SAVE_COMMENT,
          targetIds: selectedIds,
        }
      );
      setResult(res);
      if (res && res.failureCount === 0) onApplied();
    } catch (e: any) {
      setApplyError(apiErrorMessage(e, "Could not apply the changes."));
    }
  };

  const toggleId = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  const toggleField = (f: SameCodeField) =>
    setSelectedFields((prev) =>
      prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]
    );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          <Copy size={15} className="me-2" />
          Apply to every &quot;{data?.code ?? ""}&quot; on{" "}
          {data?.brandName ?? "this brand"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <Spinner animation="border" size="sm" />}
        {error && (
          <Alert variant="danger" className="small py-2">
            {apiErrorMessage(error, "Could not load the matching codes.")}
          </Alert>
        )}

        {!loading && !error && (
          <>
            <p className="small fw-semibold mb-1">
              Fields to copy — {selectedFields.length} selected
            </p>
            <p className="small text-muted mb-2">
              {unsavedFields.length > 0
                ? "Ticked are the fields you have changed but not yet saved. This code is saved first, then copied across."
                : "Nothing is unsaved on this code, so tick whichever fields you want the other codes to match."}
            </p>
            <Table size="sm" bordered className="small mb-2">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th style={{ width: "35%" }}>Field</th>
                  <th>Value that will be written</th>
                </tr>
              </thead>
              <tbody>
                {SAME_CODE_COPYABLE_FIELDS.map((field) => (
                  <tr key={field}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedFields.includes(field)}
                        onChange={() => toggleField(field)}
                      />
                    </td>
                    <td>
                      {FIELD_LABELS[field]}
                      {unsavedFields.includes(field) && (
                        <Badge bg="warning" text="dark" className="ms-1">
                          edited
                        </Badge>
                      )}
                    </td>
                    <td style={{ wordBreak: "break-word" }}>
                      {describeValue(form[field])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <p className="small text-muted">
              Code, brand, domains and countries are never copied — they are
              what makes each row a separate code.
            </p>

            <p className="small fw-semibold mb-1 mt-3">
              {siblings.length} other code{siblings.length === 1 ? "" : "s"}{" "}
              match — {selectedIds.length} selected
            </p>
            {siblings.length === 0 ? (
              <Alert variant="light" className="border small py-2">
                No other coupon on this brand carries this code, so there is
                nothing to apply to.
              </Alert>
            ) : (
              <div style={{ maxHeight: 260, overflow: "auto" }}>
                <Table size="sm" hover className="small mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 32 }}></th>
                      <th>Domains</th>
                      <th>Countries</th>
                      <th>Status</th>
                      <th>Valid to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {siblings.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedIds.includes(c.id)}
                            onChange={() => toggleId(c.id)}
                          />
                        </td>
                        <td style={{ wordBreak: "break-word" }}>
                          {c.couponDomains.length
                            ? c.couponDomains.join(", ")
                            : "—"}
                        </td>
                        <td>{c.countries.join(", ") || "—"}</td>
                        <td>{c.publishedStatus}</td>
                        <td className="text-nowrap">{formatDate(c.validTo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {applyError && (
              <Alert variant="danger" className="small py-2 mt-2 mb-0">
                {applyError}
              </Alert>
            )}
            {result && (
              <Alert
                variant={result.failureCount ? "warning" : "success"}
                className="small py-2 mt-2 mb-0"
              >
                {result.successCount} updated
                {result.failureCount > 0 && (
                  <>
                    , {result.failureCount} failed:
                    <ul className="mb-0 ps-3">
                      {result.results
                        .filter((r) => !r.success)
                        .map((r) => (
                          <li key={r.id}>
                            {r.code} — {r.error}
                          </li>
                        ))}
                    </ul>
                  </>
                )}
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide}>
          {result ? "Close" : "Cancel"}
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={
            apply.loading ||
            !!result ||
            selectedIds.length === 0 ||
            selectedFields.length === 0
          }
          onClick={confirm}
        >
          {apply.loading && (
            <Spinner animation="border" size="sm" className="me-1" />
          )}
          Apply {selectedFields.length} field
          {selectedFields.length === 1 ? "" : "s"} to {selectedIds.length} code
          {selectedIds.length === 1 ? "" : "s"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};


export const CouponEditForm: React.FC<{
  id: string;
  countryCode?: string | null;
  onSaved?: () => void;
}> = ({ id, countryCode, onSaved }) => {
  const { data, loading, error, refetch } = useGet<IKindredCouponDetail>(
    `kindred/coupons/${id}`,
    true
  );
  const { addEdit, pendingEdits, isStaging } = useEditSession();
  const saveEntity = usePut<unknown>();
  const [saveComment, setSaveComment] = useState("");
  const [form, setForm] = useState<Record<string, any>>({});
  const [showSameCode, setShowSameCode] = useState(false);

  useEffect(() => {
    if (data) {
      setForm({
        code: data.code,
        brandId: data.brandId,
        brandName: data.brandName,
        publishedStatus: data.publishedStatus,
        reason: data.reason ?? null,
        discount: data.discount,
        summary: data.summary,
        priority: data.priority,
        coverage: data.coverage,
        lastTestedAt: data.lastTestedAt,
        validFrom: data.validFrom,
        validTo: data.validTo,
        couponDomains: data.couponDomainDetails || data.couponDomains || [],
        countries:
          data.countryDetails?.map((c) => c.countryCode) ||
          data.countries ||
          [],
        overridesFlag: data.overridesFlag ?? 0,
        metadata: data.metadata
          ? {
              promoType: data.metadata.promoType,
              coverage: data.metadata.coverage,
              scope: data.metadata.scope,
              channel: data.metadata.channel,
              discountType: data.metadata.discountType,
              discounts: data.metadata.discounts,
              nonMonetaryValue: data.metadata.nonMonetaryValue,
              language: data.metadata.language,
              currency: data.metadata.currency,
              productCategory: data.metadata.productCategory,
              rawCategory: data.metadata.rawCategory,
              isStackable: data.metadata.isStackable,
              requiresReview: data.metadata.requiresReview,
              isReviewed: data.metadata.isReviewed,
              reasoning: data.metadata.reasoning,
            }
          : {},
      });
    }
  }, [data]);

  if (loading) return <Spinner animation="border" size="sm" />;
  if (error) return <Alert variant="danger">Error loading coupon</Alert>;
  if (!data) return null;

  // Shared by Save and by the same-code push, so both write exactly the same
  // diff — the confirmation window can never show something Save wouldn't do.
  const computeChanges = () => {
    const changes: Record<string, any> = {};
    if (form.code !== data.code) changes.code = form.code;
    if (form.brandId !== data.brandId) changes.brandId = form.brandId;
    if (form.publishedStatus !== data.publishedStatus)
      changes.publishedStatus = form.publishedStatus;
    if (form.reason !== data.reason) changes.reason = form.reason;
    if (form.discount !== data.discount) changes.discount = form.discount;
    if (form.summary !== data.summary) changes.summary = form.summary;
    if (form.priority !== data.priority) changes.priority = form.priority;
    if (form.coverage !== data.coverage) changes.coverage = form.coverage;
    if (form.lastTestedAt !== data.lastTestedAt)
      changes.lastTestedAt = form.lastTestedAt;
    if (form.validFrom !== data.validFrom) changes.validFrom = form.validFrom;
    if (form.validTo !== data.validTo) changes.validTo = form.validTo;
    const origDomains = data.couponDomainDetails || data.couponDomains || [];
    if (JSON.stringify(form.couponDomains) !== JSON.stringify(origDomains))
      changes.domains = form.couponDomains;
    const origCountries =
      data.countryDetails?.map((c) => c.countryCode) || data.countries || [];
    if (JSON.stringify(form.countries) !== JSON.stringify(origCountries))
      changes.countries = form.countries;
    if (form.overridesFlag !== (data.overridesFlag ?? 0))
      changes.overridesFlag = form.overridesFlag;

    const origMetadata = data.metadata
      ? {
          promoType: data.metadata.promoType,
          coverage: data.metadata.coverage,
          scope: data.metadata.scope,
          channel: data.metadata.channel,
          discountType: data.metadata.discountType,
          discounts: data.metadata.discounts,
          nonMonetaryValue: data.metadata.nonMonetaryValue,
          language: data.metadata.language,
          currency: data.metadata.currency,
          productCategory: data.metadata.productCategory,
          rawCategory: data.metadata.rawCategory,
          isStackable: data.metadata.isStackable,
          requiresReview: data.metadata.requiresReview,
          isReviewed: data.metadata.isReviewed,
          reasoning: data.metadata.reasoning,
        }
      : {};
    if (
      form.metadata &&
      Object.keys(form.metadata).length > 0 &&
      JSON.stringify(form.metadata) !== JSON.stringify(origMetadata)
    ) {
      changes.metadata = form.metadata;
    }

    return { changes, origDomains, origCountries, origMetadata };
  };

  const handleStage = async () => {
    const { changes, origDomains, origCountries, origMetadata } =
      computeChanges();

    if (!isStaging) {
      if (Object.keys(changes).length === 0) return;
      await saveEntity.execute(`kindred/coupons/${data.id}`, {
        data: changes,
        comment: saveComment.trim() || DEFAULT_SAVE_COMMENT,
      });
      setSaveComment("");
      refetch();
      onSaved?.();
      return;
    }

    addEdit({
      type: "coupon",
      id: data.id,
      entityName: data.code || data.id,
      original: {
        code: data.code,
        brandId: data.brandId,
        publishedStatus: data.publishedStatus,
        discount: data.discount,
        summary: data.summary,
        priority: data.priority,
        coverage: data.coverage,
        lastTestedAt: data.lastTestedAt,
        validFrom: data.validFrom,
        validTo: data.validTo,
        domains: origDomains,
        countries: origCountries,
        overridesFlag: data.overridesFlag ?? 0,
        metadata: origMetadata,
      },
      changes,
    });
  };

  const isPending = pendingEdits.has(`coupon:${id}`);

  return (
    <>
      <div
        className="d-flex gap-2 mb-2"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "white",
          paddingBottom: 4,
        }}
      >
        <Link
          to={`/kindred/coupons/${id}`}
          className="btn btn-outline-secondary btn-sm"
          target="_blank"
        >
          <ExternalLink size={12} className="me-1" />
          Full Detail
        </Link>
        <Link
          to={`/kindred/coupons/${id}/audit`}
          className="btn btn-outline-secondary btn-sm"
          target="_blank"
        >
          <ExternalLink size={12} className="me-1" />
          Audit Trail
        </Link>
        <Button
          variant={isPending ? "outline-success" : "primary"}
          size="sm"
          onClick={handleStage}
          disabled={saveEntity.loading}
        >
          <Save size={12} className="me-1" />
          {!isStaging
            ? saveEntity.loading
              ? "Saving..."
              : "Save"
            : isPending
              ? "Update Staged"
              : "Stage Changes"}
        </Button>
        {!isStaging && (
          <Button
            variant="outline-primary"
            size="sm"
            title="Make every identical code on this brand match this one"
            onClick={() => setShowSameCode(true)}
          >
            <Copy size={12} className="me-1" />
            Apply to same code
          </Button>
        )}
        {isPending && (
          <Badge bg="success" className="align-self-center">
            Staged
          </Badge>
        )}
      </div>

      <ApplyToSameCodeModal
        show={showSameCode}
        couponId={data.id}
        form={form}
        unsavedFields={Object.keys(computeChanges().changes)}
        comment={saveComment}
        saveSource={handleStage}
        onHide={() => setShowSameCode(false)}
        onApplied={() => {
          refetch();
          onSaved?.();
        }}
      />

      {!isStaging && (
        <SaveCommentField
          value={saveComment}
          onChange={setSaveComment}
          error={saveEntity.error}
        />
      )}

      <RecentCouponResults code={data.code} countryCode={countryCode} />

      <Table size="sm" borderless className="small mb-2">
        <tbody>
          <tr>
            <td className="text-muted">Valid From</td>
            <td>
              <Form.Control
                size="sm"
                type="datetime-local"
                value={
                  form.validFrom
                    ? form.validFrom.substring(0, 16)
                    : data.validFrom
                      ? data.validFrom.substring(0, 16)
                      : ""
                }
                onChange={(e) =>
                  setForm({
                    ...form,
                    validFrom: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td className="text-muted">Valid To</td>
            <td>
              <Form.Control
                size="sm"
                type="datetime-local"
                value={
                  form.validTo
                    ? form.validTo.substring(0, 16)
                    : data.validTo
                      ? data.validTo.substring(0, 16)
                      : ""
                }
                onChange={(e) =>
                  setForm({
                    ...form,
                    validTo: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td className="text-muted">Last Tested</td>
            <td>
              <Form.Control
                size="sm"
                type="datetime-local"
                value={
                  form.lastTestedAt
                    ? form.lastTestedAt.substring(0, 16)
                    : data.lastTestedAt
                      ? data.lastTestedAt.substring(0, 16)
                      : ""
                }
                onChange={(e) =>
                  setForm({
                    ...form,
                    lastTestedAt: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  })
                }
              />
            </td>
          </tr>
        </tbody>
      </Table>

      <BrandTypeahead
        brandId={form.brandId || data.brandId}
        brandName={form.brandName || data.brandName}
        onChange={(brandId, brandName) =>
          setForm({ ...form, brandId, brandName })
        }
      />

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Code</Form.Label>
        <Form.Control
          size="sm"
          value={form.code || ""}
          onChange={(e) => setForm({ ...form, code: e.target.value })}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Published Status</Form.Label>
        <Form.Select
          size="sm"
          value={form.publishedStatus ?? ""}
          onChange={(e) =>
            setForm({ ...form, publishedStatus: Number(e.target.value) })
          }
        >
          {publishedStatusOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      {form.publishedStatus === KindredPublishStatus.Deactivated && (
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Reason</Form.Label>
          <Form.Control
            size="sm"
            required
            value={form.reason ?? ""}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
          />
        </Form.Group>
      )}
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Discount</Form.Label>
        <Form.Control
          size="sm"
          value={form.discount || ""}
          onChange={(e) => setForm({ ...form, discount: e.target.value })}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Summary</Form.Label>
        <Form.Control
          as="textarea"
          size="sm"
          rows={2}
          value={form.summary || ""}
          onChange={(e) => setForm({ ...form, summary: e.target.value })}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Priority</Form.Label>
        <Form.Control
          size="sm"
          type="number"
          value={form.priority ?? ""}
          onChange={(e) =>
            setForm({ ...form, priority: Number(e.target.value) })
          }
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Coverage</Form.Label>
        <Form.Select
          size="sm"
          value={form.coverage ?? ""}
          onChange={(e) => {
            const coverage =
              e.target.value === "" ? null : Number(e.target.value);
            // Mirror into metadata — a disagreement between the two is exactly
            // what the pipeline flags as a coverage mismatch.
            setForm({
              ...form,
              coverage,
              metadata: { ...(form.metadata ?? {}), coverage },
            });
          }}
        >
          <option value="">—</option>
          <option value={KindredCouponCoverage.None}>Not Set</option>
          <option value={KindredCouponCoverage.SiteWide}>Site-Wide</option>
          <option value={KindredCouponCoverage.Category}>Category</option>
          <option value={KindredCouponCoverage.Product}>
            Product Specific
          </option>
        </Form.Select>
      </Form.Group>

      <TagListEditor
        label="Coupon Domains"
        normalize="lower"
        items={form.couponDomains || []}
        onChange={(couponDomains) => setForm({ ...form, couponDomains })}
        placeholder="Add domain..."
      />

      <TagListEditor
        label="Countries"
        items={form.countries || []}
        onChange={(countries) => setForm({ ...form, countries })}
        placeholder="Add country code..."
      />

      <OverrideFlagsEditor
        value={form.overridesFlag ?? 0}
        onChange={(overridesFlag) => setForm({ ...form, overridesFlag })}
        flagEnum={CouponOverridesFlag}
      />

      <MetadataEditor
        metadata={form.metadata || {}}
        onChange={(m) => setForm({ ...form, metadata: m })}
      />
    </>
  );
};

// ── Recent Coupon Results ────────────────────────────────

export const RecentCouponResults: React.FC<{
  code: string;
  countryCode?: string | null;
}> = ({ code, countryCode }) => {
  const url = code
    ? `results/coupons?code=${encodeURIComponent(code)}&limit=3${countryCode ? `&countryCode=${encodeURIComponent(countryCode)}` : ""}`
    : null;
  const { data, loading } = useGet<ICouponResultsResponse>(url, !!code);

  return (
    <Card className="mb-2 mt-2">
      <Card.Header className="py-1 small fw-semibold">
        Recent Test Results
      </Card.Header>
      <Card.Body className="py-2">
        {loading ? (
          <Spinner animation="border" size="sm" />
        ) : !data || data.results.length === 0 ? (
          <span className="text-muted small">No test results found</span>
        ) : (
          <Table size="sm" borderless className="small mb-0">
            <thead>
              <tr>
                <th>Status</th>
                <th>Savings</th>
                <th>Merchant</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((r) => (
                <tr key={r._id}>
                  <td>
                    <CouponResultBadge result={r} />
                  </td>
                  <td>
                    {r.savings > 0 ? (
                      <span className="text-success">
                        {r.testResult?.currency || ""} {r.savings.toFixed(2)}
                        {r.percentageSavings != null && (
                          <> ({r.percentageSavings.toFixed(1)}%)</>
                        )}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="text-truncate" style={{ maxWidth: 100 }}>
                    {r.merchant?.name || "—"}
                  </td>
                  <td>
                    {r.createdAt
                      ? format(new Date(r.createdAt), "MMM d, HH:mm")
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

export const CouponResultBadge: React.FC<{
  result: ICouponResultWithMerchant;
}> = ({ result }) => {
  if (result.systemError) {
    return (
      <div>
        <Badge bg="danger">
          <AlertOctagon size={10} className="me-1" />
          Error
        </Badge>
        {result.errorMessage && (
          <div className="text-danger small mt-1" style={{ fontSize: 11 }}>
            {result.errorMessage}
          </div>
        )}
      </div>
    );
  }
  if (result.confirmedFailure) {
    return (
      <div>
        <Badge bg="warning" text="dark">
          <AlertTriangle size={10} className="me-1" />
          Failed
        </Badge>
        {result.errorMessage && (
          <div className="text-muted small mt-1" style={{ fontSize: 11 }}>
            {result.errorMessage}
          </div>
        )}
      </div>
    );
  }
  if (result.applied) {
    return (
      <Badge bg="success">
        <CheckCircle size={10} className="me-1" />
        Applied
      </Badge>
    );
  }
  if (result.notApplicableToBasket) {
    return <Badge bg="secondary">N/A</Badge>;
  }
  if (result.errorMessage) {
    return (
      <div>
        <Badge bg="light" text="dark">
          Not Applied
        </Badge>
        <div className="text-muted small mt-1" style={{ fontSize: 11 }}>
          {result.errorMessage}
        </div>
      </div>
    );
  }
  return (
    <Badge bg="light" text="dark">
      Not Applied
    </Badge>
  );
};

// ── Offer Edit Form ──────────────────────────────────────

const OfferEditForm: React.FC<{ id: string; onSaved?: () => void }> = ({
  id,
  onSaved,
}) => {
  const { data, loading, error, refetch } = useGet<IKindredOfferDetail>(
    `kindred/offers/${id}`,
    true
  );
  const { addEdit, pendingEdits, isStaging } = useEditSession();
  const saveEntity = usePut<unknown>();
  const [saveComment, setSaveComment] = useState("");
  const [form, setForm] = useState<Record<string, any>>({});

  useEffect(() => {
    if (data) {
      setForm({
        brandId: data.brandId,
        brandName: data.brandName,
        publishedStatus: data.publishedStatus,
        summary: data.summary,
        priority: data.priority,
        trackingLink: data.trackingLink,
        promoLink: data.promoLink,
        coverage: data.coverage,
        hasProhibitedContents: data.hasProhibitedContents,
        lastTestedAt: data.lastTestedAt,
        validFrom: data.validFrom,
        validTo: data.validTo,
        offerDomains: data.offerDomainDetails || data.offerDomains || [],
        countries:
          data.countryDetails?.map((c) => c.countryCode) ||
          data.countries ||
          [],
        overridesFlag: data.overridesFlag ?? 0,
        metadata: data.metadata
          ? {
              promoType: data.metadata.promoType,
              coverage: data.metadata.coverage,
              scope: data.metadata.scope,
              channel: data.metadata.channel,
              discountType: data.metadata.discountType,
              discounts: data.metadata.discounts,
              nonMonetaryValue: data.metadata.nonMonetaryValue,
              language: data.metadata.language,
              currency: data.metadata.currency,
              productCategory: data.metadata.productCategory,
              rawCategory: data.metadata.rawCategory,
              isStackable: data.metadata.isStackable,
              requiresReview: data.metadata.requiresReview,
              isReviewed: data.metadata.isReviewed,
              reasoning: data.metadata.reasoning,
            }
          : {},
      });
    }
  }, [data]);

  if (loading) return <Spinner animation="border" size="sm" />;
  if (error) return <Alert variant="danger">Error loading offer</Alert>;
  if (!data) return null;

  const handleStage = async () => {
    const changes: Record<string, any> = {};
    if (form.brandId !== data.brandId) changes.brandId = form.brandId;
    if (form.publishedStatus !== data.publishedStatus)
      changes.publishedStatus = form.publishedStatus;
    if (form.summary !== data.summary) changes.summary = form.summary;
    if (form.priority !== data.priority) changes.priority = form.priority;
    if (form.trackingLink !== data.trackingLink)
      changes.trackingLink = form.trackingLink;
    if (form.promoLink !== data.promoLink) changes.promoLink = form.promoLink;
    if (form.coverage !== data.coverage) changes.coverage = form.coverage;
    if (form.hasProhibitedContents !== data.hasProhibitedContents)
      changes.hasProhibitedContents = form.hasProhibitedContents;
    if (form.lastTestedAt !== data.lastTestedAt)
      changes.lastTestedAt = form.lastTestedAt;
    if (form.validFrom !== data.validFrom) changes.validFrom = form.validFrom;
    if (form.validTo !== data.validTo) changes.validTo = form.validTo;
    const origDomains = data.offerDomainDetails || data.offerDomains || [];
    if (JSON.stringify(form.offerDomains) !== JSON.stringify(origDomains))
      changes.domains = form.offerDomains;
    const origCountries =
      data.countryDetails?.map((c) => c.countryCode) || data.countries || [];
    if (JSON.stringify(form.countries) !== JSON.stringify(origCountries))
      changes.countries = form.countries;
    if (form.overridesFlag !== (data.overridesFlag ?? 0))
      changes.overridesFlag = form.overridesFlag;

    const origMetadata = data.metadata
      ? {
          promoType: data.metadata.promoType,
          coverage: data.metadata.coverage,
          scope: data.metadata.scope,
          channel: data.metadata.channel,
          discountType: data.metadata.discountType,
          discounts: data.metadata.discounts,
          nonMonetaryValue: data.metadata.nonMonetaryValue,
          language: data.metadata.language,
          currency: data.metadata.currency,
          productCategory: data.metadata.productCategory,
          rawCategory: data.metadata.rawCategory,
          isStackable: data.metadata.isStackable,
          requiresReview: data.metadata.requiresReview,
          isReviewed: data.metadata.isReviewed,
          reasoning: data.metadata.reasoning,
        }
      : {};
    if (
      form.metadata &&
      Object.keys(form.metadata).length > 0 &&
      JSON.stringify(form.metadata) !== JSON.stringify(origMetadata)
    ) {
      changes.metadata = form.metadata;
    }

    if (!isStaging) {
      if (Object.keys(changes).length === 0) return;
      await saveEntity.execute(`kindred/offers/${data.id}`, {
        data: changes,
        comment: saveComment.trim() || DEFAULT_SAVE_COMMENT,
      });
      setSaveComment("");
      refetch();
      onSaved?.();
      return;
    }

    addEdit({
      type: "offer",
      id: data.id,
      entityName: data.summary || data.id,
      original: {
        brandId: data.brandId,
        publishedStatus: data.publishedStatus,
        summary: data.summary,
        priority: data.priority,
        trackingLink: data.trackingLink,
        promoLink: data.promoLink,
        coverage: data.coverage,
        hasProhibitedContents: data.hasProhibitedContents,
        lastTestedAt: data.lastTestedAt,
        validFrom: data.validFrom,
        validTo: data.validTo,
        domains: origDomains,
        countries: origCountries,
        overridesFlag: data.overridesFlag ?? 0,
        metadata: origMetadata,
      },
      changes,
    });
  };

  const isPending = pendingEdits.has(`offer:${id}`);

  return (
    <>
      <div
        className="d-flex gap-2 mb-2"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "white",
          paddingBottom: 4,
        }}
      >
        <Link
          to={`/kindred/offers/${id}`}
          className="btn btn-outline-secondary btn-sm"
          target="_blank"
        >
          <ExternalLink size={12} className="me-1" />
          Full Detail
        </Link>
        <Link
          to={`/kindred/offers/${id}/audit`}
          className="btn btn-outline-secondary btn-sm"
          target="_blank"
        >
          <ExternalLink size={12} className="me-1" />
          Audit Trail
        </Link>
        <Button
          variant={isPending ? "outline-success" : "primary"}
          size="sm"
          onClick={handleStage}
          disabled={saveEntity.loading}
        >
          <Save size={12} className="me-1" />
          {!isStaging
            ? saveEntity.loading
              ? "Saving..."
              : "Save"
            : isPending
              ? "Update Staged"
              : "Stage Changes"}
        </Button>
        {isPending && (
          <Badge bg="success" className="align-self-center">
            Staged
          </Badge>
        )}
      </div>

      {!isStaging && (
        <SaveCommentField
          value={saveComment}
          onChange={setSaveComment}
          error={saveEntity.error}
        />
      )}

      <Table size="sm" borderless className="small mb-2">
        <tbody>
          <tr>
            <td className="text-muted">Valid From</td>
            <td>
              <Form.Control
                size="sm"
                type="datetime-local"
                value={
                  form.validFrom
                    ? form.validFrom.substring(0, 16)
                    : data.validFrom
                      ? data.validFrom.substring(0, 16)
                      : ""
                }
                onChange={(e) =>
                  setForm({
                    ...form,
                    validFrom: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td className="text-muted">Valid To</td>
            <td>
              <Form.Control
                size="sm"
                type="datetime-local"
                value={
                  form.validTo
                    ? form.validTo.substring(0, 16)
                    : data.validTo
                      ? data.validTo.substring(0, 16)
                      : ""
                }
                onChange={(e) =>
                  setForm({
                    ...form,
                    validTo: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  })
                }
              />
            </td>
          </tr>
          <tr>
            <td className="text-muted">Last Tested</td>
            <td>
              <Form.Control
                size="sm"
                type="datetime-local"
                value={
                  form.lastTestedAt
                    ? form.lastTestedAt.substring(0, 16)
                    : data.lastTestedAt
                      ? data.lastTestedAt.substring(0, 16)
                      : ""
                }
                onChange={(e) =>
                  setForm({
                    ...form,
                    lastTestedAt: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null,
                  })
                }
              />
            </td>
          </tr>
        </tbody>
      </Table>

      <BrandTypeahead
        brandId={form.brandId || data.brandId}
        brandName={form.brandName || data.brandName}
        onChange={(brandId, brandName) =>
          setForm({ ...form, brandId, brandName })
        }
      />

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Published Status</Form.Label>
        <Form.Select
          size="sm"
          value={form.publishedStatus ?? ""}
          onChange={(e) =>
            setForm({ ...form, publishedStatus: Number(e.target.value) })
          }
        >
          {publishedStatusOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Summary</Form.Label>
        <Form.Control
          as="textarea"
          size="sm"
          rows={2}
          value={form.summary || ""}
          onChange={(e) => setForm({ ...form, summary: e.target.value })}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Priority</Form.Label>
        <Form.Control
          size="sm"
          type="number"
          value={form.priority ?? ""}
          onChange={(e) =>
            setForm({ ...form, priority: Number(e.target.value) })
          }
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Tracking Link</Form.Label>
        <Form.Control
          size="sm"
          value={form.trackingLink || ""}
          onChange={(e) => setForm({ ...form, trackingLink: e.target.value })}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Promo Link</Form.Label>
        <Form.Control
          size="sm"
          value={form.promoLink || ""}
          onChange={(e) => setForm({ ...form, promoLink: e.target.value })}
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Coverage</Form.Label>
        <Form.Select
          size="sm"
          value={form.coverage ?? ""}
          onChange={(e) => {
            const coverage =
              e.target.value === "" ? null : Number(e.target.value);
            setForm({
              ...form,
              coverage,
              metadata: { ...(form.metadata ?? {}), coverage },
            });
          }}
        >
          <option value="">—</option>
          <option value={KindredCouponCoverage.None}>Not Set</option>
          <option value={KindredCouponCoverage.SiteWide}>Site-Wide</option>
          <option value={KindredCouponCoverage.Category}>Category</option>
          <option value={KindredCouponCoverage.Product}>
            Product Specific
          </option>
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Prohibited Contents</Form.Label>
        <Form.Select
          size="sm"
          value={form.hasProhibitedContents ?? 0}
          onChange={(e) =>
            setForm({
              ...form,
              hasProhibitedContents: Number(e.target.value),
            })
          }
        >
          <option value={0}>No</option>
          <option value={1}>Yes</option>
        </Form.Select>
      </Form.Group>

      <TagListEditor
        label="Offer Domains"
        normalize="lower"
        items={form.offerDomains || []}
        onChange={(offerDomains) => setForm({ ...form, offerDomains })}
        placeholder="Add domain..."
      />

      <TagListEditor
        label="Countries"
        items={form.countries || []}
        onChange={(countries) => setForm({ ...form, countries })}
        placeholder="Add country code..."
      />

      <OverrideFlagsEditor
        value={form.overridesFlag ?? 0}
        onChange={(overridesFlag) => setForm({ ...form, overridesFlag })}
        flagEnum={OfferOverridesFlag}
      />

      <MetadataEditor
        metadata={form.metadata || {}}
        onChange={(m) => setForm({ ...form, metadata: m })}
      />
    </>
  );
};
